// MyDocumentsScreen.js
// Full-featured document manager: store, view, share, delete, rename PDFs
// Receives resume PDFs from BuildResumeScreen · import from device
// Storage: AsyncStorage (metadata) + expo-file-system (actual files)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, FlatList, Alert, Modal,
  TextInput, ScrollView, ActivityIndicator, SafeAreaView, StatusBar,
  Dimensions, Animated, RefreshControl, Share, KeyboardAvoidingView, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';

// ─── Icons ────────────────────────────────────────────────────────────────────
let Ionicons, MaterialCommunityIcons;
try {
  const iv = require('@expo/vector-icons');
  Ionicons = iv.Ionicons;
  MaterialCommunityIcons = iv.MaterialCommunityIcons;
} catch {
  const F = ({ size=20, style }) => (
    <View style={[{ width:size, height:size, borderRadius:size/2, backgroundColor:'#94a3b8' }, style]}/>
  );
  Ionicons = MaterialCommunityIcons = F;
}

// ─── Optional native libs ─────────────────────────────────────────────────────
let FileSystem, Sharing, DocumentPicker, Print, WebView;
try { FileSystem     = require('expo-file-system/legacy');                       } catch {}
try { Sharing        = require('expo-sharing');                                  } catch {}
try { DocumentPicker = require('expo-document-picker');                          } catch {}
try { Print          = require('expo-print');                                    } catch {}
try { WebView        = require('react-native-webview').WebView;                  } catch {}

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// ─── Storage key ─────────────────────────────────────────────────────────────
const DOCS_META_KEY = 'my_documents_metadata';
const DOCS_DIR      = FileSystem?.documentDirectory
  ? `${FileSystem.documentDirectory}my_documents/`
  : null;

// ─── Categories ───────────────────────────────────────────────────────────────
const CATEGORIES = [
  { key:'all',           label:'All',          icon:'folder-outline',           color:'#0e4e96' },
  { key:'resume',        label:'Resumes',      icon:'document-text-outline',    color:'#0e4e96' },
  { key:'certificate',   label:'Certificates', icon:'ribbon-outline',           color:'#D97706' },
  { key:'contract',      label:'Contracts',    icon:'contract',                 color:'#7C3AED' },
  { key:'payslip',       label:'Payslips',     icon:'cash-outline',             color:'#059669' },
  { key:'cover_letter',  label:'Cover Letters',icon:'mail-outline',             color:'#DB2777' },
  { key:'reference',     label:'References',   icon:'people-outline',           color:'#0891B2' },
  { key:'other',         label:'Other',        icon:'document-attach-outline',  color:'#64748B' },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

const formatSize = bytes => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
};

const formatDate = iso => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
  } catch { return ''; }
};

const mkId = () => `doc_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MyDocumentsScreen() {
  const navigation = useNavigation();
  const route      = useRoute();

  const [docs,        setDocs]        = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search,      setSearch]      = useState('');
  const [showSearch,  setShowSearch]  = useState(false);
  const [sortBy,      setSortBy]      = useState('date_desc'); // date_desc | date_asc | name | size
  const [viewMode,    setViewMode]    = useState('list'); // list | grid

  // Modals
  const [viewerDoc,       setViewerDoc]       = useState(null);
  const [showViewer,      setShowViewer]       = useState(false);
  const [viewerLoading,   setViewerLoading]    = useState(false);
  const [showSortModal,   setShowSortModal]    = useState(false);
  const [showRenameModal, setShowRenameModal]  = useState(false);
  const [renamingDoc,     setRenamingDoc]      = useState(null);
  const [renameValue,     setRenameValue]      = useState('');
  const [importing,       setImporting]        = useState(false);
  const [storageInfo,     setStorageInfo]      = useState({ count:0, totalSize:0 });

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── Init ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    init();
    Animated.timing(fadeAnim, { toValue:1, duration:400, useNativeDriver:true }).start();
  }, []);

  // Check if we're being called with a new doc from BuildResume
  useEffect(() => {
    const params = route?.params;
    if (params?.newDoc) {
      saveDocMetadata(params.newDoc);
      // Clear params so it doesn't re-fire
      navigation.setParams({ newDoc: undefined });
    }
  }, [route?.params?.newDoc]);

  useFocusEffect(useCallback(() => {
    loadDocs();
  }, []));

  const init = async () => {
    try {
      // Ensure documents directory exists
      if (FileSystem && DOCS_DIR) {
        const info = await FileSystem.getInfoAsync(DOCS_DIR);
        if (!info.exists) await FileSystem.makeDirectoryAsync(DOCS_DIR, { intermediates: true });
      }
      await loadDocs();
    } catch (e) { console.warn('MyDocumentsScreen init:', e?.message); }
    finally { setLoading(false); }
  };

  const loadDocs = async () => {
    try {
      const raw = await AsyncStorage.getItem(DOCS_META_KEY);
      const list = raw ? JSON.parse(raw) : [];
      // Verify files still exist (may have been cleared by OS)
      const verified = await Promise.all(list.map(async doc => {
        if (!doc.filePath || !FileSystem) return doc;
        try {
          const info = await FileSystem.getInfoAsync(doc.filePath);
          return { ...doc, exists: info.exists };
        } catch { return { ...doc, exists: false }; }
      }));
      setDocs(verified);
      calcStorageInfo(verified);
    } catch (e) { console.warn('loadDocs:', e?.message); }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadDocs();
    setRefreshing(false);
  };

  const calcStorageInfo = (list) => {
    const totalSize = list.reduce((s, d) => s + (d.size || 0), 0);
    setStorageInfo({ count: list.length, totalSize });
  };

  // ── Apply filters + sort ─────────────────────────────────────────────────────
  useEffect(() => {
    let f = [...docs];
    if (activeCategory !== 'all') f = f.filter(d => d.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(d => String(d.name ?? '').toLowerCase().includes(q));
    }
    f.sort((a, b) => {
      if (sortBy === 'name')      return a.name.localeCompare(b.name);
      if (sortBy === 'size')      return (b.size||0) - (a.size||0);
      if (sortBy === 'date_asc')  return new Date(a.dateAdded) - new Date(b.dateAdded);
      return new Date(b.dateAdded) - new Date(a.dateAdded); // date_desc
    });
    setFiltered(f);
  }, [docs, activeCategory, search, sortBy]);

  // ── Save metadata ────────────────────────────────────────────────────────────
  const saveDocMetadata = async (newDoc) => {
    try {
      const raw = await AsyncStorage.getItem(DOCS_META_KEY);
      const list = raw ? JSON.parse(raw) : [];
      // Replace if same id, else prepend
      const idx = list.findIndex(d => d.id === newDoc.id);
      if (idx >= 0) list[idx] = newDoc; else list.unshift(newDoc);
      await AsyncStorage.setItem(DOCS_META_KEY, JSON.stringify(list));
      setDocs(list);
      calcStorageInfo(list);
    } catch (e) { console.warn('saveDocMetadata:', e?.message); }
  };

  // ── Import document from device ──────────────────────────────────────────────
  const importDocument = async () => {
    if (!DocumentPicker) {
      Alert.alert('Install required', 'npx expo install expo-document-picker');
      return;
    }
    setImporting(true);
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*', 'application/msword',
               'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.[0]) return;

      const asset = result.assets[0];
      const id    = mkId();
      const ext   = asset.name.split('.').pop() || 'pdf';
      const destName = `${id}.${ext}`;
      const destPath = DOCS_DIR ? `${DOCS_DIR}${destName}` : null;

      // Copy to documents directory
      let filePath = asset.uri;
      if (FileSystem && destPath) {
        await FileSystem.copyAsync({ from: asset.uri, to: destPath });
        filePath = destPath;
      }

      // Detect category from filename
      const lower = String(asset.name ?? '').toLowerCase();
      let category = 'other';
      if (lower.includes('resume') || lower.includes('cv'))         category = 'resume';
      else if (lower.includes('cert') || lower.includes('diploma')) category = 'certificate';
      else if (lower.includes('contract') || lower.includes('agreement')) category = 'contract';
      else if (lower.includes('payslip') || lower.includes('payroll'))    category = 'payslip';
      else if (lower.includes('cover') || lower.includes('letter'))       category = 'cover_letter';
      else if (lower.includes('reference') || lower.includes('referee'))  category = 'reference';

      const doc = {
        id,
        name:      asset.name.replace(/\.[^/.]+$/, ''), // name without extension
        fileName:  asset.name,
        filePath,
        mimeType:  asset.mimeType || 'application/pdf',
        size:      asset.size || 0,
        category,
        dateAdded: new Date().toISOString(),
        source:    'imported',
      };

      await saveDocMetadata(doc);
      Alert.alert('✅ Imported', `"${doc.name}" added to your documents.`);
    } catch (e) {
      if (!e?.message?.includes('cancelled')) Alert.alert('Error', 'Could not import document.');
    } finally { setImporting(false); }
  };

  // ── Open / view document ─────────────────────────────────────────────────────
  const openDocument = async (doc) => {
    const path = doc.filePath;
    if (!path) { Alert.alert('Error', 'File path not found.'); return; }

    // Check file exists
    if (FileSystem) {
      try {
        const info = await FileSystem.getInfoAsync(path);
        if (!info.exists) {
          Alert.alert('File not found', 'This file no longer exists on the device. It may have been deleted.');
          return;
        }
      } catch {}
    }

    // Try to share/open with native viewer first
    if (Sharing) {
      try {
        const available = await Sharing.isAvailableAsync();
        if (available) {
          await Sharing.shareAsync(path, {
            mimeType: doc.mimeType || 'application/pdf',
            dialogTitle: doc.name,
            UTI: 'com.adobe.pdf',
          });
          return;
        }
      } catch {}
    }

    // Fallback: show in-app viewer
    setViewerDoc(doc);
    setShowViewer(true);
  };

  // ── Share document ────────────────────────────────────────────────────────────
  const shareDocument = async (doc) => {
    if (!Sharing || !doc.filePath) {
      Alert.alert('Cannot share', 'Sharing is not available on this device.');
      return;
    }
    try {
      await Sharing.shareAsync(doc.filePath, {
        mimeType: doc.mimeType || 'application/pdf',
        dialogTitle: `Share ${doc.name}`,
        UTI: 'com.adobe.pdf',
      });
    } catch (e) { Alert.alert('Error', 'Could not share document.'); }
  };

  // ── Delete document ───────────────────────────────────────────────────────────
  const deleteDocument = (doc) => {
    Alert.alert(
      'Delete Document',
      `Remove "${doc.name}" from your documents? This cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            // Delete physical file
            if (FileSystem && doc.filePath) {
              try { await FileSystem.deleteAsync(doc.filePath, { idempotent: true }); } catch {}
            }
            // Remove from metadata
            const raw  = await AsyncStorage.getItem(DOCS_META_KEY);
            const list = raw ? JSON.parse(raw) : [];
            const updated = list.filter(d => d.id !== doc.id);
            await AsyncStorage.setItem(DOCS_META_KEY, JSON.stringify(updated));
            setDocs(updated);
            calcStorageInfo(updated);
          } catch (e) { Alert.alert('Error', 'Could not delete document.'); }
        }},
      ]
    );
  };

  // ── Rename document ───────────────────────────────────────────────────────────
  const startRename = (doc) => {
    setRenamingDoc(doc);
    setRenameValue(doc.name);
    setShowRenameModal(true);
  };

  const confirmRename = async () => {
    if (!renameValue.trim()) { Alert.alert('Invalid name', 'Name cannot be empty.'); return; }
    try {
      const raw  = await AsyncStorage.getItem(DOCS_META_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const updated = list.map(d => d.id === renamingDoc.id ? { ...d, name: renameValue.trim() } : d);
      await AsyncStorage.setItem(DOCS_META_KEY, JSON.stringify(updated));
      setDocs(updated);
      setShowRenameModal(false);
    } catch { Alert.alert('Error', 'Could not rename.'); }
  };

  // ── Change category ───────────────────────────────────────────────────────────
  const changeCategory = (doc, newCat) => {
    Alert.alert('Move to Category', `Move "${doc.name}" to ${CAT_MAP[newCat]?.label || newCat}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Move', onPress: async () => {
        try {
          const raw  = await AsyncStorage.getItem(DOCS_META_KEY);
          const list = raw ? JSON.parse(raw) : [];
          const updated = list.map(d => d.id === doc.id ? { ...d, category: newCat } : d);
          await AsyncStorage.setItem(DOCS_META_KEY, JSON.stringify(updated));
          setDocs(updated);
        } catch {}
      }},
    ]);
  };

  // ── Doc options ────────────────────────────────────────────────────────────────
  const showOptions = (doc) => {
    Alert.alert(doc.name, `${formatSize(doc.size)} · ${formatDate(doc.dateAdded)}`, [
      { text: '👁  Open / View',    onPress: () => openDocument(doc)          },
      { text: '↗  Share',           onPress: () => shareDocument(doc)         },
      { text: '✏️  Rename',          onPress: () => startRename(doc)           },
      { text: '📁  Move to Category',onPress: () => showCategoryPicker(doc)   },
      { text: '🗑  Delete',          style:'destructive', onPress:()=>deleteDocument(doc) },
      { text: 'Cancel',              style: 'cancel' },
    ]);
  };

  const showCategoryPicker = (doc) => {
    const opts = CATEGORIES.filter(c => c.key !== 'all').map(c => ({
      text: c.label,
      onPress: () => changeCategory(doc, c.key),
    }));
    Alert.alert('Move to Category', 'Select a category:', [...opts, { text:'Cancel', style:'cancel' }]);
  };

  // ── Computed storage ─────────────────────────────────────────────────────────
  const catCounts = CATEGORIES.reduce((acc, c) => {
    acc[c.key] = c.key === 'all' ? docs.length : docs.filter(d => d.category === c.key).length;
    return acc;
  }, {});

  // ─── RENDERS ─────────────────────────────────────────────────────────────────

  const DocIcon = ({ mimeType, size = 36 }) => {
    const isImg = mimeType?.startsWith('image');
    const isDoc = mimeType?.includes('word') || mimeType?.includes('document');
    const name  = isImg ? 'image-outline' : isDoc ? 'document-text-outline' : 'document-outline';
    const color = isImg ? '#DB2777' : isDoc ? '#0e4e96' : '#EF4444';
    return <Ionicons name={name} size={size} color={color}/>;
  };

  const renderListDoc = ({ item: doc }) => {
    const meta = CAT_MAP[doc.category] || CAT_MAP.other;
    return (
      <TouchableOpacity style={s.docRow} onPress={() => openDocument(doc)} onLongPress={() => showOptions(doc)}>
        <View style={[s.docRowIcon, { backgroundColor: meta.color + '18' }]}>
          <DocIcon mimeType={doc.mimeType} size={26}/>
        </View>
        <View style={{ flex:1 }}>
          <Text style={s.docName} numberOfLines={1}>{doc.name}</Text>
          <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:3 }}>
            <View style={[s.catBadge, { backgroundColor: meta.color + '20' }]}>
              <Text style={[s.catBadgeT, { color: meta.color }]}>{meta.label}</Text>
            </View>
            <Text style={s.docMeta}>{formatSize(doc.size)} · {formatDate(doc.dateAdded)}</Text>
          </View>
          {doc.source === 'resume' && (
            <Text style={s.docSource}>📄 Generated from BuildResume</Text>
          )}
        </View>
        <View style={s.docRowActions}>
          <TouchableOpacity style={s.docActionBtn} onPress={() => shareDocument(doc)}>
            <Ionicons name="share-outline" size={17} color="#64748B"/>
          </TouchableOpacity>
          <TouchableOpacity style={s.docActionBtn} onPress={() => showOptions(doc)}>
            <Ionicons name="ellipsis-vertical" size={17} color="#64748B"/>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderGridDoc = ({ item: doc }) => {
    const meta = CAT_MAP[doc.category] || CAT_MAP.other;
    return (
      <TouchableOpacity style={s.gridCard} onPress={() => openDocument(doc)} onLongPress={() => showOptions(doc)}>
        <View style={[s.gridIconWrap, { backgroundColor: meta.color + '15' }]}>
          <DocIcon mimeType={doc.mimeType} size={38}/>
          <View style={[s.gridCatDot, { backgroundColor: meta.color }]}/>
        </View>
        <Text style={s.gridName} numberOfLines={2}>{doc.name}</Text>
        <Text style={s.gridMeta}>{formatSize(doc.size)}</Text>
        <Text style={s.gridDate}>{formatDate(doc.dateAdded)}</Text>
      </TouchableOpacity>
    );
  };

  const ListHeader = () => (
    <View style={s.listHeader}>
      {/* Storage info */}
      <View style={s.storageRow}>
        <View style={s.storageItem}>
          <Text style={s.storageNum}>{storageInfo.count}</Text>
          <Text style={s.storageLbl}>Documents</Text>
        </View>
        <View style={s.storageDivider}/>
        <View style={s.storageItem}>
          <Text style={s.storageNum}>{formatSize(storageInfo.totalSize)}</Text>
          <Text style={s.storageLbl}>Total Size</Text>
        </View>
        <View style={s.storageDivider}/>
        <View style={s.storageItem}>
          <Text style={s.storageNum}>{docs.filter(d => d.category === 'resume').length}</Text>
          <Text style={s.storageLbl}>Resumes</Text>
        </View>
      </View>

      {/* Category chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.catScroll}>
        {CATEGORIES.map(cat => {
          const isA   = activeCategory === cat.key;
          const count = catCounts[cat.key] || 0;
          return (
            <TouchableOpacity key={cat.key} style={[s.catChip, isA && { backgroundColor: cat.color, borderColor: cat.color }]}
              onPress={() => setActiveCategory(cat.key)}>
              <Ionicons name={cat.icon} size={13} color={isA ? '#FFF' : cat.color}/>
              <Text style={[s.catChipT, isA && { color:'#FFF' }]}>{cat.label}</Text>
              {count > 0 && <View style={[s.catCount, { backgroundColor: isA?'rgba(255,255,255,0.3)':cat.color+'22' }]}>
                <Text style={[s.catCountT, { color: isA?'#FFF':cat.color }]}>{count}</Text>
              </View>}
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Result count */}
      <View style={s.resultRow}>
        <Text style={s.resultCount}>{filtered.length} document{filtered.length !== 1 ? 's' : ''}</Text>
        <TouchableOpacity style={s.sortBtn} onPress={() => setShowSortModal(true)}>
          <Ionicons name="swap-vertical-outline" size={15} color="#0e4e96"/>
          <Text style={s.sortBtnT}>Sort</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const EmptyState = () => (
    <View style={s.empty}>
      <Ionicons name="folder-open-outline" size={68} color="#CBD5E1"/>
      <Text style={s.emptyT}>
        {search ? 'No results found' : activeCategory !== 'all' ? `No ${CAT_MAP[activeCategory]?.label}` : 'No documents yet'}
      </Text>
      <Text style={s.emptyS}>
        {search
          ? 'Try a different search term'
          : 'Import a document or save your resume as PDF'}
      </Text>
      {!search && (
        <View style={{ flexDirection:'row', gap:10, marginTop:20 }}>
          <TouchableOpacity style={s.emptyBtn} onPress={importDocument} disabled={importing}>
            {importing ? <ActivityIndicator size={14} color="#FFF"/> : <Ionicons name="cloud-upload-outline" size={16} color="#FFF"/>}
            <Text style={s.emptyBtnT}>Import</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.emptyBtn, { backgroundColor:'#0e4e96' }]} onPress={() => navigation.navigate('BuildResume')}>
            <Ionicons name="document-text-outline" size={16} color="#FFF"/>
            <Text style={s.emptyBtnT}>Build Resume</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) return (
    <SafeAreaView style={s.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>
      <View style={s.hdr}>
        <TouchableOpacity style={s.hdrBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#FFF"/>
        </TouchableOpacity>
        <Text style={s.hdrTitle}>My Documents</Text>
        <View style={{ width:36 }}/>
      </View>
      <View style={s.loadWrap}>
        <ActivityIndicator size={36} color="#0e4e96"/>
        <Text style={s.loadT}>Loading documents…</Text>
      </View>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={s.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>

      {/* Header */}
      <View style={s.hdr}>
        <TouchableOpacity style={s.hdrBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#FFF"/>
        </TouchableOpacity>
        <Text style={s.hdrTitle}>My Documents</Text>
        <View style={{ flexDirection:'row', gap:6 }}>
          <TouchableOpacity style={s.hdrBtn} onPress={() => setShowSearch(p => !p)}>
            <Ionicons name={showSearch ? 'close' : 'search'} size={20} color="#FFF"/>
          </TouchableOpacity>
          <TouchableOpacity style={s.hdrBtn} onPress={() => setViewMode(p => p==='list'?'grid':'list')}>
            <Ionicons name={viewMode==='list' ? 'grid-outline' : 'list-outline'} size={20} color="#FFF"/>
          </TouchableOpacity>
        </View>
      </View>

      {/* Search bar */}
      {showSearch && (
        <View style={s.searchBar}>
          <Ionicons name="search-outline" size={17} color="#94A3B8"/>
          <TextInput style={s.searchIn} placeholder="Search documents…" value={search}
            onChangeText={setSearch} placeholderTextColor="#94A3B8" autoFocus/>
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={17} color="#94A3B8"/></TouchableOpacity> : null}
        </View>
      )}

      {/* Document list */}
      <Animated.View style={[{ flex:1, backgroundColor:'#F8FAFC' }, { opacity: fadeAnim }]}>
        <FlatList
          data={filtered}
          key={viewMode} // force re-render when switching grid/list
          numColumns={viewMode === 'grid' ? 2 : 1}
          renderItem={viewMode === 'grid' ? renderGridDoc : renderListDoc}
          keyExtractor={item => item.id}
          ListHeaderComponent={ListHeader}
          ListEmptyComponent={EmptyState}
          contentContainerStyle={viewMode === 'grid' ? s.gridList : s.list}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']}/>}
        />
      </Animated.View>

      {/* Import FAB */}
      <View style={s.fabContainer}>
        <TouchableOpacity style={[s.fab, { backgroundColor:'#8B5CF6' }]} onPress={importDocument} disabled={importing}>
          {importing ? <ActivityIndicator size={20} color="#FFF"/> : <Ionicons name="cloud-upload-outline" size={22} color="#FFF"/>}
        </TouchableOpacity>
        <TouchableOpacity style={[s.fab, { backgroundColor:'#0e4e96', marginTop:10 }]} onPress={() => navigation.navigate('BuildResume')}>
          <Ionicons name="document-text-outline" size={22} color="#FFF"/>
        </TouchableOpacity>
      </View>

      {/* ── Sort Modal ─────────────────────────────────────────────────────── */}
      <Modal visible={showSortModal} transparent animationType="fade" onRequestClose={() => setShowSortModal(false)}>
        <TouchableOpacity style={s.mOverlay} activeOpacity={1} onPress={() => setShowSortModal(false)}>
          <View style={s.sortCard}>
            <Text style={s.sortCardTitle}>Sort By</Text>
            {[
              { key:'date_desc', label:'Newest First',  icon:'calendar-outline'    },
              { key:'date_asc',  label:'Oldest First',  icon:'calendar-clear-outline' },
              { key:'name',      label:'Name (A–Z)',    icon:'text-outline'        },
              { key:'size',      label:'Largest First', icon:'resize-outline'      },
            ].map(opt => (
              <TouchableOpacity key={opt.key} style={[s.sortOpt, sortBy===opt.key && s.sortOptA]}
                onPress={() => { setSortBy(opt.key); setShowSortModal(false); }}>
                <Ionicons name={opt.icon} size={18} color={sortBy===opt.key?'#0e4e96':'#64748B'}/>
                <Text style={[s.sortOptT, sortBy===opt.key && { color:'#0e4e96', fontWeight:'700' }]}>{opt.label}</Text>
                {sortBy===opt.key && <Ionicons name="checkmark" size={18} color="#0e4e96"/>}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ── Rename Modal ───────────────────────────────────────────────────── */}
      <Modal visible={showRenameModal} transparent animationType="fade" onRequestClose={() => setShowRenameModal(false)}>
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={[s.mOverlay, { justifyContent:'center' }]}>
          <View style={s.renameCard}>
            <Text style={s.renameTitle}>Rename Document</Text>
            <TextInput style={s.renameInput} value={renameValue} onChangeText={setRenameValue}
              placeholder="Document name" placeholderTextColor="#94A3B8" autoFocus selectTextOnFocus/>
            <View style={s.renameFooter}>
              <TouchableOpacity style={s.renameGhost} onPress={() => setShowRenameModal(false)}>
                <Text style={s.renameGhostT}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.renamePrimary} onPress={confirmRename}>
                <Text style={s.renamePrimaryT}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── PDF Viewer Modal (fallback when Sharing unavailable) ─────────────── */}
      <Modal visible={showViewer} animationType="slide" onRequestClose={() => setShowViewer(false)}>
        <SafeAreaView style={{ flex:1, backgroundColor:'#1E1E1E' }}>
          <View style={s.viewerHdr}>
            <TouchableOpacity style={s.hdrBtn} onPress={() => setShowViewer(false)}>
              <Ionicons name="close" size={22} color="#FFF"/>
            </TouchableOpacity>
            <Text style={[s.hdrTitle, { fontSize:15 }]} numberOfLines={1}>{viewerDoc?.name}</Text>
            <TouchableOpacity style={s.hdrBtn} onPress={() => viewerDoc && shareDocument(viewerDoc)}>
              <Ionicons name="share-outline" size={20} color="#FFF"/>
            </TouchableOpacity>
          </View>
          {/* Show instructions to open externally */}
          <View style={{ flex:1, justifyContent:'center', alignItems:'center', padding:32 }}>
            <Ionicons name="document-outline" size={80} color="#4B5563"/>
            <Text style={{ fontSize:18, fontWeight:'700', color:'#F9FAFB', marginTop:20, textAlign:'center' }}>
              {viewerDoc?.name}
            </Text>
            <Text style={{ fontSize:14, color:'#9CA3AF', marginTop:8, textAlign:'center', lineHeight:22 }}>
              PDF documents open in your device's native PDF viewer.{'\n'}Tap the button below to open it.
            </Text>
            <TouchableOpacity
              style={{ marginTop:28, backgroundColor:'#0e4e96', borderRadius:14, paddingHorizontal:28, paddingVertical:14, flexDirection:'row', alignItems:'center', gap:8 }}
              onPress={() => { setShowViewer(false); if (viewerDoc) openDocument(viewerDoc); }}>
              <Ionicons name="open-outline" size={20} color="#FFF"/>
              <Text style={{ color:'#FFF', fontWeight:'700', fontSize:15 }}>Open Document</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{ marginTop:14, borderRadius:14, paddingHorizontal:28, paddingVertical:12, flexDirection:'row', alignItems:'center', gap:8, borderWidth:1, borderColor:'#374151' }}
              onPress={() => viewerDoc && shareDocument(viewerDoc)}>
              <Ionicons name="share-social-outline" size={18} color="#9CA3AF"/>
              <Text style={{ color:'#9CA3AF', fontWeight:'600', fontSize:14 }}>Share</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:         { flex:1, backgroundColor:'#0e4e96' },
  loadWrap:     { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#F8FAFC' },
  loadT:        { marginTop:14, fontSize:15, color:'#64748B', fontWeight:'500' },

  // Header
  hdr:          { flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:12, backgroundColor:'#0e4e96', gap:8 },
  hdrBtn:       { width:36, height:36, borderRadius:10, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  hdrTitle:     { flex:1, textAlign:'center', color:'#FFF', fontSize:18, fontWeight:'700' },

  // Search
  searchBar:    { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', paddingHorizontal:14, paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#E2E8F0', gap:8 },
  searchIn:     { flex:1, fontSize:14, color:'#0F172A' },

  // List
  list:         { paddingHorizontal:14, paddingBottom:100 },
  gridList:     { paddingHorizontal:10, paddingBottom:100 },

  // List header
  listHeader:   { backgroundColor:'#F8FAFC', paddingBottom:4 },
  storageRow:   { flexDirection:'row', backgroundColor:'#FFF', marginHorizontal:14, marginTop:14, borderRadius:14, borderWidth:1, borderColor:'#E2E8F0', padding:16 },
  storageItem:  { flex:1, alignItems:'center' },
  storageNum:   { fontSize:20, fontWeight:'900', color:'#0e4e96' },
  storageLbl:   { fontSize:11, color:'#94A3B8', fontWeight:'600', marginTop:2 },
  storageDivider:{ width:1, backgroundColor:'#E2E8F0', marginHorizontal:8 },
  catScroll:    { paddingHorizontal:14, paddingVertical:12, gap:8 },
  catChip:      { flexDirection:'row', alignItems:'center', paddingHorizontal:12, paddingVertical:7, borderRadius:20, backgroundColor:'#FFF', borderWidth:1, borderColor:'#E2E8F0', gap:5 },
  catChipT:     { fontSize:12, fontWeight:'600', color:'#374151' },
  catCount:     { borderRadius:10, paddingHorizontal:6, paddingVertical:2, marginLeft:2 },
  catCountT:    { fontSize:10, fontWeight:'800' },
  resultRow:    { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:14, paddingBottom:10 },
  resultCount:  { fontSize:13, color:'#94A3B8', fontWeight:'600' },
  sortBtn:      { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:12, paddingVertical:6, borderRadius:8, backgroundColor:'#EFF6FF', borderWidth:1, borderColor:'#BFDBFE' },
  sortBtnT:     { fontSize:12, fontWeight:'700', color:'#0e4e96' },

  // List doc row
  docRow:       { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', borderRadius:14, padding:14, marginBottom:10, borderWidth:1, borderColor:'#E2E8F0', gap:12, elevation:1 },
  docRowIcon:   { width:48, height:48, borderRadius:12, justifyContent:'center', alignItems:'center', flexShrink:0 },
  docName:      { fontSize:15, fontWeight:'700', color:'#0F172A' },
  docMeta:      { fontSize:11, color:'#94A3B8' },
  docSource:    { fontSize:11, color:'#0e4e96', fontStyle:'italic', marginTop:2 },
  catBadge:     { borderRadius:10, paddingHorizontal:8, paddingVertical:2 },
  catBadgeT:    { fontSize:10, fontWeight:'700' },
  docRowActions:{ flexDirection:'row', gap:4 },
  docActionBtn: { width:32, height:32, borderRadius:8, backgroundColor:'#F8FAFC', justifyContent:'center', alignItems:'center', borderWidth:1, borderColor:'#E2E8F0' },

  // Grid
  gridCard:     { flex:1, backgroundColor:'#FFF', borderRadius:14, margin:5, padding:14, borderWidth:1, borderColor:'#E2E8F0', alignItems:'center', elevation:1 },
  gridIconWrap: { width:64, height:64, borderRadius:16, justifyContent:'center', alignItems:'center', marginBottom:10, position:'relative' },
  gridCatDot:   { position:'absolute', top:0, right:0, width:12, height:12, borderRadius:6, borderWidth:2, borderColor:'#FFF' },
  gridName:     { fontSize:13, fontWeight:'700', color:'#0F172A', textAlign:'center', lineHeight:18 },
  gridMeta:     { fontSize:11, color:'#94A3B8', marginTop:4 },
  gridDate:     { fontSize:10, color:'#CBD5E1', marginTop:2 },

  // Empty
  empty:        { alignItems:'center', paddingVertical:50, paddingHorizontal:30 },
  emptyT:       { fontSize:16, fontWeight:'800', color:'#0F172A', marginTop:14 },
  emptyS:       { fontSize:13, color:'#94A3B8', textAlign:'center', marginTop:6, lineHeight:20 },
  emptyBtn:     { flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:20, paddingVertical:12, borderRadius:12, backgroundColor:'#8B5CF6' },
  emptyBtnT:    { color:'#FFF', fontWeight:'700', fontSize:14 },

  // FAB
  fabContainer: { position:'absolute', bottom:28, right:18 },
  fab:          { width:52, height:52, borderRadius:26, justifyContent:'center', alignItems:'center', elevation:6, shadowColor:'#000', shadowOpacity:0.25, shadowRadius:8, shadowOffset:{width:0,height:3} },

  // Sort modal
  mOverlay:     { flex:1, backgroundColor:'rgba(0,0,0,0.45)', justifyContent:'flex-end', padding:14 },
  sortCard:     { backgroundColor:'#FFF', borderRadius:18, padding:16 },
  sortCardTitle:{ fontSize:16, fontWeight:'800', color:'#0F172A', marginBottom:12 },
  sortOpt:      { flexDirection:'row', alignItems:'center', paddingVertical:13, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:12 },
  sortOptA:     { backgroundColor:'#EFF6FF', borderRadius:10, paddingHorizontal:8, marginHorizontal:-8 },
  sortOptT:     { flex:1, fontSize:14, color:'#374151', fontWeight:'500' },

  // Rename modal
  renameCard:   { backgroundColor:'#FFF', borderRadius:18, padding:20, marginHorizontal:20 },
  renameTitle:  { fontSize:17, fontWeight:'800', color:'#0F172A', marginBottom:14 },
  renameInput:  { borderWidth:1, borderColor:'#E2E8F0', borderRadius:12, paddingHorizontal:14, paddingVertical:12, fontSize:15, color:'#0F172A', backgroundColor:'#F8FAFC', marginBottom:16 },
  renameFooter: { flexDirection:'row', gap:10 },
  renameGhost:  { flex:1, paddingVertical:12, borderRadius:12, backgroundColor:'#F1F5F9', alignItems:'center' },
  renameGhostT: { fontSize:14, fontWeight:'700', color:'#374151' },
  renamePrimary:{ flex:2, paddingVertical:12, borderRadius:12, backgroundColor:'#0e4e96', alignItems:'center' },
  renamePrimaryT:{ fontSize:14, fontWeight:'700', color:'#FFF' },

  // Viewer
  viewerHdr:    { flexDirection:'row', alignItems:'center', backgroundColor:'#111827', paddingHorizontal:14, paddingVertical:12, gap:8, borderBottomWidth:1, borderBottomColor:'#374151' },
});
